import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CallButton from "../components/CallButton";
import { Users, UserCheck, DollarSign, TrendingUp, Activity, CalendarClock, PhoneCall, AlarmClock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatCard, Select, PageLoader, ChartTooltip } from "../components/ui";
import { money, countryFlag, initials } from "../lib/constants";
import { format, formatDistanceToNow, isPast } from "date-fns";

const RANGES = [
  { key: "total", label: "Total" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
];

function rangeDates(key) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (key === "this_month") return [new Date(now.getFullYear(), now.getMonth(), 1), now];
  if (key === "last_month") return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)];
  if (key === "this_week") { const d = startOfDay(now); const day = (d.getDay() + 6) % 7; return [new Date(d.getTime() - day * 86400000), now]; }
  if (key === "last_week") { const d = startOfDay(now); const day = (d.getDay() + 6) % 7; const monday = new Date(d.getTime() - day * 86400000); return [new Date(monday.getTime() - 7 * 86400000), new Date(monday.getTime() - 1000)]; }
  return [null, null];
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.is_admin;
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState({ new_leads: 0, new_clients: 0, deposits: 0 });
  const [range, setRange] = useState("total");
  const [agent, setAgent] = useState("all");
  const [agents, setAgents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reminders, setReminders] = useState([]);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { api.get("/dashboard/reminders").then((r) => setReminders(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) { api.get("/users/simple").then((r) => setAgents(r.data)); api.get("/analytics", { params: { days: 30 } }).then((r) => setAnalytics(r.data)).catch(() => {}); } }, [isAdmin]);

  useEffect(() => {
    const [start, end] = rangeDates(range);
    api.get("/dashboard/performance", {
      params: { agent_id: agent, start: start ? start.toISOString() : undefined, end: end ? end.toISOString() : undefined },
    }).then((r) => setPerf(r.data));
  }, [range, agent]);

  // Only worth marking ownership when the list actually mixes owners: a
  // privileged account sees everybody's callbacks, an agent only their own.
  const others = reminders.filter((r) => !r.mine).length;
  const mixed = others > 0;

  if (!stats) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white">
          Welcome back, <span className="text-indigo-600 dark:text-indigo-400">{user?.full_name}</span>
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{isAdmin ? "Admin Dashboard" : "Agent Workspace"} • {format(new Date(), "EEEE, MMMM d, yyyy")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Leads" value={stats.total_leads.toLocaleString()} icon={Users} testid="kpi-leads" />
        <StatCard label="Total Clients" value={stats.total_clients.toLocaleString()} icon={UserCheck} accent="text-emerald-600 dark:text-emerald-400" testid="kpi-clients" />
        <StatCard label="Total Deposits" value={money(stats.total_deposits)} icon={DollarSign} accent="text-amber-600 dark:text-amber-400" testid="kpi-deposits" />
        <StatCard label="Conversions" value={stats.conversions.toLocaleString()} icon={TrendingUp} accent="text-cyan-600 dark:text-cyan-400" testid="kpi-conversions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-500" /> Performance</h3>
            {isAdmin && (
              <div className="w-44"><Select value={agent} onChange={setAgent} options={[{ value: "all", label: "All Agents" }, ...agents.map((a) => ({ value: a.id, label: a.full_name }))]} testid="perf-agent" /></div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${range === r.key ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}>{r.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-500/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-400">New Leads</div>
              <div className="text-2xl font-bold font-display text-slate-900 dark:text-white mt-1" data-testid="perf-leads">{perf.new_leads.toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-500/5 p-4">
              <div className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-400">New Clients</div>
              <div className="text-2xl font-bold font-display text-slate-900 dark:text-white mt-1" data-testid="perf-clients">{perf.new_clients.toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-amber-100 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-500/5 p-4">
              <div className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400">Deposits</div>
              <div className="text-2xl font-bold font-display text-slate-900 dark:text-white mt-1" data-testid="perf-deposits">{money(perf.deposits)}</div>
            </div>
          </div>

          <h3 className="font-semibold font-display text-slate-900 dark:text-white mt-6 mb-3">Last 30 Days</h3>
          {analytics && analytics.series.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={analytics.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" width={28} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="leads" stroke="#6366f1" strokeWidth={2} dot={false} name="Leads" />
                <Line type="monotone" dataKey="clients" stroke="#10b981" strokeWidth={2} dot={false} name="Clients" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No recent activity to chart</div>
          )}
        </div>

        {/* Appointment Reminders */}
        <div className="card p-6 flex flex-col" data-testid="reminders-panel">
          <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-amber-500" /> Appointment Reminders
          </h3>
          <p className="text-xs text-slate-400 mb-4">
            {reminders.length} scheduled call{reminders.length === 1 ? "" : "s"}
            {mixed && (
              <span data-testid="reminders-others-count">
                {" "}&middot; <span className="font-semibold text-slate-500 dark:text-slate-300">{others}</span> for other agents
              </span>
            )}
          </p>
          <div className="flex-1 space-y-2 overflow-y-auto max-h-[460px] pr-1">
            {reminders.length === 0 && (
              <div className="h-40 flex flex-col items-center justify-center text-center text-slate-400">
                <AlarmClock className="w-8 h-8 mb-2 opacity-50" />
                <span className="text-sm">No upcoming appointments</span>
              </div>
            )}
            {reminders.map((r) => {
              const due = r.callback_at ? new Date(r.callback_at) : null;
              const overdue = due && isPast(due);
              return (
                <div
                  key={r.id}
                  onClick={() => nav(`/${r.client_type}s/${r.id}`)}
                  data-testid={`reminder-${r.id}`}
                  className={`group rounded-lg border p-3 cursor-pointer transition-colors ${overdue ? "border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-500/5 hover:bg-rose-50 dark:hover:bg-rose-500/10" : "border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-500/5 hover:bg-amber-50 dark:hover:bg-amber-500/10"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base">{countryFlag(r.country)}</span>
                      <span className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">{r.name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {mixed && (r.mine ? (
                        <span
                          data-testid={`reminder-owner-${r.id}`}
                          className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300"
                        >
                          You
                        </span>
                      ) : (
                        <span
                          data-testid={`reminder-owner-${r.id}`}
                          title={`${r.agent_name || "Unassigned"}'s appointment, not yours`}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-200/90 dark:bg-slate-700/80 py-0.5 pl-0.5 pr-2"
                        >
                          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-500 dark:bg-slate-400 text-[8px] font-bold text-white dark:text-slate-900">
                            {initials(r.agent_name)}
                          </span>
                          <span className="max-w-[84px] truncate text-[10px] font-semibold text-slate-600 dark:text-slate-200">
                            {r.agent_name || "Unassigned"}
                          </span>
                        </span>
                      ))}
                      {r.phone && (
                        <CallButton phone={r.phone} clientId={r.id} className="btn-ghost p-1.5 text-indigo-600 dark:text-indigo-400" testid={`reminder-call-${r.id}`} title="Call">
                          <PhoneCall className="w-4 h-4" />
                        </CallButton>
                      )}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span className={overdue ? "text-rose-600 dark:text-rose-400 font-medium" : "text-amber-600 dark:text-amber-400 font-medium"}>
                      {due ? format(due, "MMM d, h:mm a") : ""}
                    </span>
                    <span className="text-slate-400">{due ? formatDistanceToNow(due, { addSuffix: true }) : ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
